/**
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { VirtualSurveyorConnectionSettings } from './settings.js'

let webChatInstance = null;
// Resolver of a pending wait for the encrypted request the host builds for the CopilotBridge (see
// askHostForBridgeRequest). The request is { message, keyId }: an AES-GCM blob holding the user's access token, or the
// anonymous session's device data, plus a timestamp. It is only valid for a few minutes, is only ever sent to the
// bridge, and must never be logged. This page never sees the access token.
let bridgeRequestWaiter = null;
// Set by createCustomStore once the current conversation is connected. Lets
// the WPF host push Global variable updates (e.g. license status changing
// mid-session) via window.updateChatBotContext without tearing down and
// restarting the whole conversation.
let sendContextUpdate = null;
const styleOptions = {
// ─── Transcript / main background ────────────────────────────────
    backgroundColor:                    '#666666',  // entire webchat background

    // ─── Bot bubbles ──────────────────────────────────────────────────
    bubbleBackground:                   '#777777',
    bubbleTextColor:                    '#EEEEEE',
    bubbleBorderColor:                  '#C49330',
    bubbleBorderWidth:                  1,
    bubbleBorderRadius:                 4,
    bubbleMaxWidth:                     680,

    // ─── User bubbles ─────────────────────────────────────────────────
    bubbleFromUserBackground:           '#777777',
    bubbleFromUserTextColor:            '#EEEEEE',
    bubbleFromUserBorderColor:          '#444444',
    bubbleFromUserBorderWidth:          1,
    bubbleFromUserBorderRadius:         4,

    // ─── Send box (input area at the bottom) ──────────────────────────
    sendBoxBackground:                  '#444444',
    sendBoxTextColor:                   '#EEEEEE',
    sendBoxBorderTop:                   '1px solid #C49330',
    sendBoxButtonColor:                 '#C49330',      // send/attachment icons
    sendBoxButtonColorOnFocus:          '#8D723',
    sendBoxButtonColorOnHover:          '#A88232',
    sendBoxButtonShadeColorOnHover:     'transparent',   // gradient applied via CSS
    sendBoxButtonShadeColorOnFocus:     'transparent',   // gradient applied via CSS
    sendBoxHeight:                      40,

    // ─── Suggested actions (quick-reply buttons) ─────────────────────
    suggestedActionBackgroundColor:     '#444444',
    suggestedActionTextColor:           '#EEEEEE',
    suggestedActionBorderColor:         '#C49330',
    suggestedActionBorderRadius:        4,
    suggestedActionBorderWidth:         1,
    suggestedActionBackgroundColorOnHover: '#666666',

    // ─── Typography ───────────────────────────────────────────────────
    primaryFont:                        'BonMono, Arial, sans-serif',
    bubbleFontSize:                     14,

    // ─── Timestamp / avatar ───────────────────────────────────────────
    timestampColor:                     '#CCCCCC',
    // showAvatarInGroup:                  'sender',   // 'sender' = show avatar next to every bot message
    // botAvatarInitials:                    '',
    // botAvatarImage:                     'favicon.png',
    // botAvatarBackgroundColor:           'transparent',

    // ─── Send box ─────────────────────────────────────────────────────
    hideUploadButton:                   true,

    // ─── Typing indicator ─────────────────────────────────────────────
    showTyping:                         true,
    // Animated logo in ochre (#C49330): a 300×90 (3×) animated WebP with transparency, rendered from the designer's
    // SVG. index.css scales it to the width of WebChat's typing box (default 64×20).
    //typingAnimationWidth:               100,
    //typingAnimationHeight:              30,
    typingAnimationBackgroundImage:     `url("./vs-loading.webp")`
};
const settings = new VirtualSurveyorConnectionSettings();

// JSON for console logs with the bridge request removed (only present when running outside the host, see
// askHostForBridgeRequest). Use this for any log of userData / chatBotContext.
function redactForLog(value) {
    if (value && typeof value === 'object' && 'bridgeRequest' in value) {
        return JSON.stringify({ ...value, bridgeRequest: value.bridgeRequest ? '[redacted]' : value.bridgeRequest });
    }
    return JSON.stringify(value);
}

/**
 * Waits for window.chatBotContext to be populated by the WPF host.
 * The host injects the variable via AddScriptToExecuteOnDocumentCreatedAsync,
 * but the module bundle may execute before that injection is fully evaluated.
 * Polls every 50 ms and resolves as soon as the object is present, falling
 * back to safe defaults after `timeoutMs` milliseconds.
 */
function waitForChatBotContext(timeoutMs = 5000) {
    const t0 = performance.now();
    console.log(`[chatBotContext] waiting — DOMContentLoaded: ${document.readyState}, t=0ms`);
    console.log(`[chatBotContext] window.chatBotContext at t=0:`, redactForLog(window.chatBotContext));

    return new Promise((resolve) => {
        // Check immediately — if AddScriptToExecuteOnDocumentCreatedAsync ran
        // before this module executed, the value is already here.
        if (window.chatBotContext) {
            const elapsed = (performance.now() - t0).toFixed(1);
            console.log(`[chatBotContext] resolved IMMEDIATELY at t=${elapsed}ms:`, redactForLog(window.chatBotContext));
            resolve(window.chatBotContext);
            return;
        }

        const interval = 50;
        let ticks = 0;

        const poll = setInterval(() => {
            ticks++;
            const elapsed = (performance.now() - t0).toFixed(1);

            if (window.chatBotContext) {
                clearInterval(poll);
                console.log(`[chatBotContext] resolved via POLL after ${ticks} tick(s) (t=${elapsed}ms):`, redactForLog(window.chatBotContext));
                resolve(window.chatBotContext);
                return;
            }

            // Log every 10 ticks (~500ms) so we can see how long it's polling
            if (ticks % 10 === 0) {
                console.log(`[chatBotContext] still waiting… tick=${ticks}, t=${elapsed}ms, window.chatBotContext=${window.chatBotContext}`);
            }

            if (elapsed >= timeoutMs) {
                clearInterval(poll);
                const fallback = {
                    userName: "Anonymous",
                    userEmail: "anon@example.com",
                    userLanguage: navigator.language || "en-US"
                };
                console.warn(`[chatBotContext] TIMEOUT after t=${elapsed}ms — falling back to:`, JSON.stringify(fallback));
                resolve(fallback);
            }
        }, interval);
    });
}

// Maps the WPF host's userData/chatBotContext field names to the matching
// Copilot Studio Global variable names sent via the pvaSetContext event.
const CONTEXT_FIELD_MAP = {
    userFirstName: "AuthenticatedUserFirstName",
    userLastName: "AuthenticatedUserLastName",
    userEmail: "AuthenticatedUserEmail",
    userCountry: "AuthenticatedUserCountry",
    userLicense: "AuthenticatedUserLicense",
    userLicenseStatus: "AuthenticatedUserLicenseStatus",
    userLicenseStatusMessage: "AuthenticatedUserLicenseStatusMessage",
    // Passes the 2-letter ISO language code (e.g. "en", "nl", "pt") so
    // Copilot Studio topics and generative AI can use it.
    userLanguage: "AuthenticatedUserLanguage"
};

// Only includes keys actually present on userData, so a partial update (e.g.
// just userLicenseStatus) doesn't clobber other Global variables with undefined.
function buildContextPayload(userData) {
    const payload = {};
    for (const [sourceKey, targetKey] of Object.entries(CONTEXT_FIELD_MAP)) {
        if (userData[sourceKey] !== undefined) {
            payload[targetKey] = userData[sourceKey];
        }
    }
    return payload;
}

function createCustomStore(userData) {
    console.log(`[createCustomStore] store created with userData:`, redactForLog(userData));
    return window.WebChat.createStore(
        {},
        ({ dispatch }) =>
            (next) =>
                (action) => {
                    if (action.type === "DIRECT_LINE/CONNECT_FULFILLED") {
                        const contextPayload = buildContextPayload(userData);
                        contextPayload.AuthenticatedUserLanguage = userData.userLanguage || "en";
                        console.log(`[createCustomStore] DIRECT_LINE/CONNECT_FULFILLED — dispatching pvaSetContext:`, JSON.stringify(contextPayload));
                        dispatch({
                            type: "WEB_CHAT/SEND_EVENT",
                            payload: {
                                name: "pvaSetContext",
                                value: contextPayload,
                            },
                        });

                        // Exposes a way to push Global variable updates for the rest of
                        // this conversation (see window.updateChatBotContext below).
                        sendContextUpdate = (partialUserData) => {
                            const updatePayload = buildContextPayload(partialUserData);
                            if (Object.keys(updatePayload).length === 0) {
                                console.warn(`[updateChatBotContext] No recognized fields in`, redactForLog(partialUserData), `— nothing sent.`);
                                return;
                            }
                            console.log(`[updateChatBotContext] dispatching pvaSetContext:`, JSON.stringify(updatePayload));
                            dispatch({
                                type: "WEB_CHAT/SEND_EVENT",
                                payload: {
                                    name: "pvaSetContext",
                                    value: updatePayload,
                                },
                            });
                        };

                        // Delay startConversation so the server has time to process
                        // the pvaSetContext event and populate global variables before
                        // the Conversation Start topic runs and greets the user.
                        setTimeout(() => {
                            console.log(`[createCustomStore] dispatching startConversation (delayed)`);
                            dispatch({
                                type: "DIRECT_LINE/POST_ACTIVITY",
                                meta: { method: "keyboard" },
                                payload: {
                                    activity: {
                                        channelData: { postBack: true },
                                        name: "startConversation",
                                        type: "event",
                                    },
                                },
                            });
                        }, 500);
                    }
                    return next(action);
                }
    );
}

// Tells the WPF host the chat UI has actually mounted, via WebView2's
// host<->page messaging channel. The WPF side waits for this (instead of
// e.g. NavigationCompleted, which fires before this module has finished its
// own async setup) before showing the webview after a reload, so the user
// never sees a flash of the previous page's stale content.
function notifyHostChatReady() {
    if (window.chrome && window.chrome.webview) {
        console.log(`[notifyHostChatReady] posting chatReady to host`);
        window.chrome.webview.postMessage('chatReady');
    }
}

// Asks the host for a fresh encrypted request, right when one is needed, so it carries a fresh timestamp and a
// current access token. The host answers by calling window.updateBridgeRequest. `serverTime` is the bridge's clock
// after it rejected a request: the host uses it to correct for a PC clock that is off. Resolves with
// { message, keyId }, or null when the host has none (no key in this build, or a signed-in user without an access
// token) or does not answer in time.
function askHostForBridgeRequest(serverTime = null, timeoutMs = 15000) {
    if (!(window.chrome && window.chrome.webview)) {
        // Not hosted (local development in a browser): use what chatBotContext carries, if anything
        return Promise.resolve(window.chatBotContext?.bridgeRequest ?? null);
    }

    return new Promise((resolve) => {
        const timer = setTimeout(() => {
            bridgeRequestWaiter = null;
            resolve(null);
        }, timeoutMs);
        bridgeRequestWaiter = (request) => {
            clearTimeout(timer);
            bridgeRequestWaiter = null;
            resolve(request);
        };
        window.chrome.webview.postMessage({ type: 'bridgeRequestNeeded', serverTime });
    });
}

// Failure talking to the CopilotBridge. `kind` drives the message shown to the user; `anonymous` comes from the bridge
// (rate-limit and busy answers say whether the caller was an anonymous session), so the page can suggest signing in.
class BridgeError extends Error {
    constructor(kind, retryAfterSeconds, anonymous = false) {
        super(`CopilotBridge request failed: ${kind}`);
        this.kind = kind;
        this.retryAfterSeconds = retryAfterSeconds;
        this.anonymous = anonymous;
    }
}

// Exchanges a fresh encrypted request from the host for a Direct Line conversation token. The Copilot Studio secret
// lives only in the bridge; this client holds nothing secret. One retry when the bridge cannot verify the request,
// because the usual cause is a clock offset the host can correct with the bridge's time.
// Returns { token, directLineDomain, expiresIn, conversationId }.
async function requestConversationToken(retry = true, serverTime = null) {
    const request = await askHostForBridgeRequest(serverTime);
    if (!request || !request.message || !request.keyId) {
        throw new BridgeError('unauthorized');
    }

    let response;
    try {
        response = await fetch(settings.tokenEndpoint, {
            method: 'POST',
            headers: { 'X-App-Key-Id': request.keyId, 'Content-Type': 'text/plain' },
            body: request.message,
        });
    } catch (err) {
        throw new BridgeError('unavailable');
    }

    // The error body says what went wrong and, where relevant, whether the caller was an anonymous session
    const readBody = () => response.json().catch(() => ({}));

    if (response.status === 401) {
        const body = await readBody();
        if (body.error === 'unauthorized') {
            // The request was fine but the access token inside it was not: the user has to sign in again
            throw new BridgeError('unauthorized');
        }
        if (retry) {
            return requestConversationToken(false, body.serverTime ?? null);
        }
        throw new BridgeError('unverified');
    }

    if (response.status === 403) {
        const body = await readBody();
        throw new BridgeError(body.error === 'blocked' ? 'blocked' : 'unauthorized');
    }

    if (response.status === 429) {
        // retryAfterSeconds is in the body because browsers cannot read Retry-After cross-origin
        const body = await readBody();
        throw new BridgeError('rate-limited', body.retryAfterSeconds, body.anonymous === true);
    }

    if (!response.ok) {
        // 503 covers the kill switch and the global cap of anonymous sessions
        const body = await readBody();
        throw new BridgeError('unavailable', body.retryAfterSeconds, body.anonymous === true);
    }

    const info = await response.json().catch(() => null);
    if (!info || !info.token || !info.directLineDomain) {
        throw new BridgeError('unavailable');
    }

    return info;
}

function chatErrorMessage(err) {
    if (err instanceof BridgeError) {
        if (err.kind === 'unauthorized') {
            return "We couldn't verify your session. Please sign in again and reopen Smart Help.";
        }
        if (err.kind === 'unverified') {
            return "We couldn't start Smart Help. Please check that your computer's date and time are correct, then restart the application.";
        }
        if (err.kind === 'blocked') {
            return 'Smart Help is not available for this device.';
        }
        if (err.kind === 'rate-limited') {
            const minutes = err.retryAfterSeconds ? Math.max(1, Math.ceil(err.retryAfterSeconds / 60)) : null;
            const hint = err.anonymous ? ' Signing in to your account also lets you continue.' : '';
            return (minutes
                ? `You've started too many chats in a short time. Please try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`
                : "You've started too many chats in a short time. Please try again later.") + hint;
        }
        if (err.kind === 'unavailable' && err.anonymous) {
            return 'Smart Help is busy or temporarily unavailable. Please try again later, or sign in to your account.';
        }
    }
    return 'Smart Help is temporarily unavailable. Please try again later.';
}

// Renders a message into #webchat. Anything rendered there also hides the loading overlay (see index.css).
function showChatError(err) {
    const container = document.getElementById("webchat");
    container.replaceChildren();

    const message = document.createElement('div');
    message.setAttribute('role', 'alert');
    message.textContent = chatErrorMessage(err);
    message.style.cssText =
        'margin:24px;padding:16px;border:1px solid #C49330;border-radius:4px;' +
        'background:#777777;color:#EEEEEE;font:14px BonMono, Arial, sans-serif;';
    container.appendChild(message);
}

async function restartConversation(userData) {
    try {
        const conversationInfo = await requestConversationToken();

        const newDirectLine = window.WebChat.createDirectLine({
            domain: conversationInfo.directLineDomain,
            token: conversationInfo.token,
        });

        const webchatElement = document.getElementById("webchat");
        webChatInstance = window.WebChat.renderWebChat(
            {
                directLine: newDirectLine,
                locale: userData.userLanguage || navigator.language || "en-US",
                styleOptions,
                store: createCustomStore(userData),
            },
            webchatElement
        );
        notifyHostChatReady();
    } catch (err) {
        console.error("Failed to restart conversation:", err);
        showChatError(err);
        notifyHostChatReady();
    }
}

async function initializeChat(userData) {
    try {
        const conversationInfo = await requestConversationToken();

        const directLine = window.WebChat.createDirectLine({
            domain: conversationInfo.directLineDomain,
            token: conversationInfo.token,
        });

        webChatInstance = window.WebChat.renderWebChat(
            {
                directLine,
                locale: userData.userLanguage || navigator.language || "en-US",
                styleOptions,
                store: createCustomStore(userData)
            },
            document.getElementById("webchat")
        );

        document.querySelector('#webchat > *').focus();
        notifyHostChatReady();
    } catch (err) {
        console.error("Failed to initialize chat:", err);
        // Still tell the host the page is ready, otherwise it keeps the webview hidden and the user sees nothing
        showChatError(err);
        notifyHostChatReady();
    }
}

// The host's answer to 'bridgeRequestNeeded' (see askHostForBridgeRequest): a freshly built encrypted request, or
// nothing (null) when it has none. Called via CoreWebView2.ExecuteScriptAsync. Never log the message.
window.updateBridgeRequest = function (message, keyId) {
    if (bridgeRequestWaiter) {
        bridgeRequestWaiter(message && keyId ? { message, keyId } : null);
    }
};

// Lets the WPF host push updated Global variables mid-conversation, e.g.
// window.updateChatBotContext({ userLicenseStatus: "Expired" }) via
// CoreWebView2.ExecuteScriptAsync when a license check changes after the
// chat has already initialized. Only recognized CONTEXT_FIELD_MAP keys are
// sent, and any field omitted from partialUserData is left untouched.
window.updateChatBotContext = function (partialUserData) {
    if (!sendContextUpdate) {
        console.warn(`[updateChatBotContext] Called before the conversation is connected — ignoring:`, redactForLog(partialUserData));
        return;
    }
    sendContextUpdate(partialUserData);
};

// Hosts that serve the TEST instance (custom domain, the Static Web App's default domain, local dev).
// Listed explicitly so an unknown host, including Production's, never shows the badge.
const TEST_HOSTNAMES = new Set([
    "smart-help-test.virtual-surveyor.com",
    "kind-wave-04da89903.3.azurestaticapps.net",
    "localhost",
]);

// Small fixed "TEST" badge so testers can tell the instances apart. Styled in index.css.
function showEnvironmentBadge() {
    if (!TEST_HOSTNAMES.has(window.location.hostname)) {
        return;
    }
    const badge = document.createElement("div");
    badge.id = "environment-badge";
    badge.textContent = "TEST";
    badge.title = `Test instance (${window.location.hostname})`;
    document.body.appendChild(badge);
}

showEnvironmentBadge();

// Wait for the WPF host to inject window.chatBotContext before starting,
// then initialize the chat with the resolved user data.
(async () => {
    try {
        console.log(`[init] module IIFE started — readyState: ${document.readyState}`);
        const userData = await waitForChatBotContext();
        console.log(`[init] proceeding with userData:`, redactForLog(userData));
        await initializeChat(userData);
    } catch (err) {
        console.error('[init] Failed to initialize:', err);
    }
})();